import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useT } from '../services/i18n';
import ZoomGallery, { isTouch } from './ZoomGallery';

const ProductGallery = ({ images, name, discount, activeImage, onChange }) => {
  const { t } = useT();
  const [zoom, setZoom] = useState(false);
  const sw = useRef({ x: 0, y: 0, moved: false });
  const count = images.length;
  const prev = () => onChange((activeImage - 1 + count) % count);
  const next = () => onChange((activeImage + 1) % count);

  useEffect(() => {
    if (count < 2) return undefined;
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const onTouchStart = (e) => { sw.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, moved: false }; };
  const onTouchMove = (e) => { if (Math.abs(e.touches[0].clientX - sw.current.x) > 10 || Math.abs(e.touches[0].clientY - sw.current.y) > 10) sw.current.moved = true; };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - sw.current.x, dy = e.changedTouches[0].clientY - sw.current.y;
    if (count > 1 && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) { e.preventDefault(); dx < 0 ? next() : prev(); return; }
    if (!sw.current.moved) { e.preventDefault(); setZoom(true); }
  };
  const openZoom = () => { if (isTouch()) setZoom(true); };

  return (
    <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6" data-testid="product-gallery">
      <div className="relative h-80 flex items-center justify-center group" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} style={{ touchAction: 'pan-y' }} data-testid="product-gallery-stage">
        {discount && (
          <span className="absolute top-0 left-0 z-10 bg-red-500 text-white text-sm font-bold px-3 py-1 rounded">-{discount}%</span>
        )}
        <img
          key={activeImage}
          data-testid="product-main-image"
          src={images[activeImage]}
          alt={`${name} — photo ${activeImage + 1}`}
          onClick={openZoom}
          className="max-h-full max-w-full object-contain animate-in fade-in duration-200 select-none"
          draggable={false}
        />
        <span className="md:hidden absolute top-0 right-0 inline-flex items-center gap-1 rounded-full bg-gray-900/60 text-white text-[10px] px-2 py-0.5 pointer-events-none" data-testid="product-gallery-zoom-hint"><ZoomIn size={11} /> Toucher pour zoomer</span>
        {count > 1 && (
          <>
            <button
              type="button"
              data-testid="gallery-prev"
              onClick={prev}
              onTouchEnd={(e) => e.stopPropagation()}
              aria-label={t('gallery.prev')}
              className="absolute left-0 top-1/2 -translate-y-1/2 bg-white/90 border border-gray-200 shadow rounded-full p-2 text-gray-700 hover:text-orange-600 hover:border-orange-300 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              data-testid="gallery-next"
              onClick={next}
              onTouchEnd={(e) => e.stopPropagation()}
              aria-label={t('gallery.next')}
              className="absolute right-0 top-1/2 -translate-y-1/2 bg-white/90 border border-gray-200 shadow rounded-full p-2 text-gray-700 hover:text-orange-600 hover:border-orange-300 transition-colors"
            >
              <ChevronRight size={20} />
            </button>
            <span data-testid="gallery-counter" className="absolute bottom-0 right-0 bg-gray-900/70 text-white text-xs px-2 py-0.5 rounded-full">
              {activeImage + 1} / {count}
            </span>
          </>
        )}
      </div>
      {count > 1 && (
        <div className="flex gap-2 mt-4 justify-center flex-wrap" data-testid="gallery-thumbnails">
          {images.map((img, idx) => (
            <button
              key={`${img}-${idx}`}
              type="button"
              data-testid={`gallery-thumb-${idx}`}
              onClick={() => onChange(idx)}
              aria-current={idx === activeImage}
              className={`w-16 h-16 border-2 rounded p-1 bg-white transition-colors ${idx === activeImage ? 'border-orange-500' : 'border-gray-200 hover:border-orange-300'}`}
            >
              <img src={img} alt="" className="w-full h-full object-contain" />
            </button>
          ))}
        </div>
      )}
      {zoom && <ZoomGallery items={images.map((src, i) => ({ src, alt: `${name} — ${i + 1}/${count}`, shareTitle: name, url: window.location.href }))} index={activeImage} onIndexChange={onChange} onClose={() => setZoom(false)} testId="product-gallery" />}
    </div>
  );
};

export default ProductGallery;
